import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, KPI, DataTable, GhostButton, PrimaryButton, Select } from "@/components/Primitives";
import StatusPill from "@/components/StatusPill";
import TedRunModal from "@/components/TedRunModal";
import DueRunModal from "@/components/DueRunModal";
import AutomationOpsPanel from "@/components/AutomationOpsPanel";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkle, MagnifyingGlass, Robot } from "@phosphor-icons/react";

export default function TenderDiscoveryControl() {
  const [data, setData] = useState({ rules: [], candidates: [], runs: [], intel: [] });
  const [suggestion, setSuggestion] = useState(null);
  const [busyRule, setBusyRule] = useState(null);
  const [tedBusyRule, setTedBusyRule] = useState(null);
  const [tedResult, setTedResult] = useState(null);
  const [dueBusy, setDueBusy] = useState(false);
  const [dueResult, setDueResult] = useState(null);
  const [opsRefresh, setOpsRefresh] = useState(0);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [a, b, c, d] = await Promise.all([
        api.get("/collections/search_rules"),
        api.get("/collections/candidate_opportunities"),
        api.get("/collections/search_runs"),
        api.get("/collections/search_intelligence"),
      ]);
      setData({ rules: a.data, candidates: b.data, runs: c.data, intel: d.data });
    } catch (e) { /* request failed; keep current/default data */ }
  };

  useEffect(() => {
    let active = true;
    (async () => { const _ = await load(); if (!active) return; })();
    return () => { active = false; };
  }, []);

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

  const setAutomation = async (rule, patch) => {
    try {
      await api.patch(`/search-rules/${rule.id}/automation`, patch);
      toast.success("Automation updated");
      await load();
      setOpsRefresh((n) => n + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update automation"); }
  };

  const runDue = async () => {
    setDueBusy(true);
    toast.message("Running due TED searches…");
    try {
      const r = await api.post("/search-runs/run-due");
      const d = r.data;
      setDueResult(d);
      if (d.already_running) toast.error("A due-search cycle is already running");
      else toast.success(`Due run · ${d.rules_run}/${d.rules_checked} rules · created ${d.candidates_created}`);
      await load();
      setOpsRefresh((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run due failed");
    } finally { setDueBusy(false); }
  };

  const runTed = async (ruleId, name) => {
    setTedBusyRule(ruleId);
    toast.message(`Running TED search for "${name}"…`);
    try {
      const r = await api.post(`/search-rules/${ruleId}/run-ted`, {});
      const d = r.data;
      setTedResult(d);
      if (d.run_status === "Failed") toast.error("TED run failed — see details");
      else toast.success(`TED run · created ${d.candidates_created} · possible dups ${d.possible_duplicates_created} · skipped ${d.exact_duplicates_skipped}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "TED search failed");
    } finally { setTedBusyRule(null); }
  };

  const suggest = async (ruleId, name) => {
    setBusyRule(ruleId);
    try {
      const r = await api.post("/ai/suggest-queries", { search_rule_id: ruleId });
      setSuggestion({ name, ...r.data });
    } catch (e) { toast.error("AI suggest failed"); }
    finally { setBusyRule(null); }
  };

  const queue = data.candidates.filter((c) => ["New Candidate", "Needs Review"].includes(c.review_status));
  const highFit = data.candidates.filter((c) => (c.suggested_fit_score || 0) >= 70);
  const dups = data.candidates.filter((c) => ["Possible Duplicate", "Duplicate"].includes(c.duplicate_check));
  const needsVerify = data.candidates.filter((c) => c.source_verification_status !== "Verified"
    && (c.requires_web_verification === true || c.cannot_import_as_opportunity === true
        || c.manual_download_required === true || c.manual_review_priority === "High"));
  const failed = data.runs.filter((r) => ["Failed", "Needs Review"].includes(r.run_status));
  const intelReview = data.intel.filter((i) => ["Needs Review", "Needs Update"].includes(i.review_status));

  return (
    <div>
      <PageHeader overline="Discovery" title="Tender Discovery Control" sub="Run, review, and approve discovered tenders before they enter the pipeline." />
      <div className="p-8 space-y-8">
        <section className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <KPI label="Active Rules" value={data.rules.filter((r) => r.active).length} hint={`${data.rules.length} total`} />
          <KPI label="Review Queue" value={queue.length} accent />
          <KPI label="Needs Verification" value={needsVerify.length} />
          <KPI label="High-Fit Candidates" value={highFit.length} />
          <KPI label="Possible Duplicates" value={dups.length} />
          <KPI label="Intel Needs Review" value={intelReview.length} />
        </section>

        <section>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Active Search Rules · TED Automation</div>
            <PrimaryButton onClick={runDue} disabled={dueBusy} data-testid="run-due-now-btn">
              <Robot size={12} className="inline mr-1.5" />{dueBusy ? "Running Due…" : "Run Due Searches Now"}
            </PrimaryButton>
          </div>
          <DataTable
            onRowClick={(r) => nav(`/search-rules`)}
            columns={[
              { key: "rule_name", label: "Rule" },
              { key: "product_category", label: "Category" },
              { key: "_auto", label: "Auto", render: (r) => (
                <span onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setAutomation(r, { automation_enabled: !r.automation_enabled })}
                    className={`text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${r.automation_enabled ? "border-[#00E676]/40 text-[#00E676] bg-[#00E676]/10" : "border-white/15 text-zinc-500 hover:text-zinc-300"}`}
                    data-testid={`automation-toggle-${r.id}`}
                  >
                    {r.automation_enabled ? "On" : "Off"}
                  </button>
                </span>
              ) },
              { key: "_freq", label: "Frequency", render: (r) => (
                <span onClick={(e) => e.stopPropagation()}>
                  <Select
                    value={r.search_frequency || "Daily"}
                    onChange={(e) => setAutomation(r, { search_frequency: e.target.value })}
                    data-testid={`automation-freq-${r.id}`}
                  >
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Manual">Manual</option>
                  </Select>
                </span>
              ) },
              { key: "next_run_at", label: "Next Run", render: (r) => <span className="font-mono-data text-[11px] text-zinc-400" data-testid={`next-run-${r.id}`}>{fmtDate(r.next_run_at)}</span> },
              { key: "_last", label: "Last Auto Run", render: (r) => (
                <span className="flex items-center gap-2" title={r.last_automated_error || ""} data-testid={`last-run-${r.id}`}>
                  {r.last_automated_run_status ? <StatusPill value={r.last_automated_run_status} /> : <span className="text-zinc-600 text-[11px]">—</span>}
                  {r.last_automated_run_at && <span className="text-[10px] text-zinc-500 font-mono-data">{fmtDate(r.last_automated_run_at)}</span>}
                  {r.last_automated_error && <span className="text-[#FF3B30] text-[10px]">⚠</span>}
                </span>
              ) },
              { key: "_ai", label: "", render: (r) => (
                <span className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                  <GhostButton onClick={() => runTed(r.id, r.rule_name)} disabled={tedBusyRule === r.id} data-testid={`run-ted-${r.id}`}>
                    <MagnifyingGlass size={11} className="inline mr-1" />{tedBusyRule === r.id ? "Running…" : "Run TED"}
                  </GhostButton>
                  <GhostButton onClick={() => suggest(r.id, r.rule_name)} disabled={busyRule === r.id} data-testid={`suggest-${r.id}`}>
                    <Sparkle size={11} className="inline mr-1" />{busyRule === r.id ? "…" : "Suggest"}
                  </GhostButton>
                </span>
              ) },
            ]}
            rows={data.rules}
          />
        </section>

        <AutomationOpsPanel reloadSignal={opsRefresh} onRunDue={runDue} dueBusy={dueBusy} />

        {suggestion && (
          <Card className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">AI Query Suggestions</div>
                <div className="font-heading text-lg">{suggestion.name}</div>
              </div>
              <GhostButton onClick={() => setSuggestion(null)}>Close</GhostButton>
            </div>
            {suggestion.queries?.length ? (
              <ul className="space-y-1.5 text-xs font-mono-data text-zinc-300">
                {suggestion.queries.map((q, i) => <li key={i} className="border-l-2 border-[#FFB300] pl-3">{q}</li>)}
              </ul>
            ) : <div className="text-zinc-500 text-xs">{suggestion.error || "No suggestions returned."}</div>}
            {suggestion.portals?.length > 0 && (
              <div className="mt-3 text-[11px] text-zinc-400">Portals: <span className="font-mono-data text-zinc-300">{suggestion.portals.join(", ")}</span></div>
            )}
          </Card>
        )}

        <section>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Candidate Review Queue</div>
            <PrimaryButton onClick={() => nav("/candidates")}>Open Queue →</PrimaryButton>
          </div>
          <DataTable
            onRowClick={(r) => nav(`/candidates/${r.id}`)}
            columns={[
              { key: "candidate_name", label: "Candidate" },
              { key: "product_category", label: "Category" },
              { key: "country", label: "Country" },
              { key: "deadline", label: "Deadline", mono: true },
              { key: "suggested_fit_score", label: "Score", mono: true },
              { key: "duplicate_check", label: "Dup", render: (r) => <StatusPill value={r.duplicate_check} /> },
              { key: "review_status", label: "Status", render: (r) => <StatusPill value={r.review_status} /> },
            ]}
            rows={queue}
          />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Recent Search Runs</div>
            <DataTable
              columns={[
                { key: "search_run_name", label: "Run" },
                { key: "run_date", label: "Date", mono: true },
                { key: "candidates_added", label: "Added", mono: true },
                { key: "run_status", label: "Status", render: (r) => <StatusPill value={r.run_status} /> },
              ]}
              rows={data.runs.slice(0, 6)}
            />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Intelligence Needing Review</div>
            <DataTable
              columns={[
                { key: "intelligence_name", label: "Intelligence" },
                { key: "product_category", label: "Category" },
                { key: "review_status", label: "Status", render: (r) => <StatusPill value={r.review_status} /> },
                { key: "confidence", label: "Confidence", render: (r) => <StatusPill value={r.confidence} /> },
              ]}
              rows={intelReview.slice(0, 6)}
            />
          </div>
        </section>
      </div>
      <TedRunModal open={!!tedResult} result={tedResult} onClose={() => setTedResult(null)} />
      <DueRunModal open={!!dueResult} result={dueResult} onClose={() => setDueResult(null)} />
    </div>
  );
}
